import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, RadioGroup, Radio } from "@mui/material";
import * as Mui from "@mui/material";
import {
  themes,
  scrollTop,
  tableRowSize,
  stickyHeader,
  infoStyle,
} from "services/constants";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import {
  payrollCycleHeader,
  upcomingPayrollCycleHeader,
} from "../../../PayRollHeaders";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { showToast } from "services/utils/Status";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import sessionHandling from "services/utils/notificationUtils";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { paymentOptions } from "pages/PayRoll/PayRollHeaders";
import { cardStyle } from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";
import { customAPI } from "services/utils/CustomAPI";
import { usePayrollCycleRun } from "./PayrollCycleRunContext";

export const PaySchedule = ({ setIsClickSchedule }) => {
  const { domain, topbarCompanyId, token } = useSelector(
    (state) => state.tokenReducer
  );
  const {
    setPayScheduleData,
    initiatePayrun,
    setInitiatePayrun,
    paymentBasedOn,
    setPaymentBasedOn,
    payScheduleId,
    setPayScheduleId,
    setGetCompanyId,
    activeTab,
    setViewPayRunScreen,
    setLogData,
    setIsProcessing,
    setProcessingMessage,
    setSocketCompleted,
    setRecallSummaryApi,
    setRecallEmployeeApi,
    setSocket,
    setTaskProgress,
    hanldeLoader,
    setHanldeLoader,
    socket,
  } = usePayrollCycleRun();
  const containerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [upcomingRowsPerPage, setUpcomingRowsPerPage] = useState(25);
  const [apiData, setApiData] = useState([]);
  const [payrunData, setPayrunData] = useState([]);
  const [upcomingData, setUpcomingData] = useState([]);
  const [filterUpcomingData, setFilterUpcomingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const getApiEndpoint = useCallback(() => {
    const baseUrl = `${domain}payroll/operations/payrollprocessing/payrollruncycle/payschedules/`;
    const companyId = topbarCompanyId;
    switch (activeTab) {
      case 0:
        return `${baseUrl}?status=Run&company_id=${companyId}`;
      case 1:
        return `${baseUrl}?status=Awaiting Approval&company_id=${companyId}`;
      case 2:
        return `${baseUrl}?status=Completed&company_id=${companyId}`;
      default:
        return `${baseUrl}?status=Run&company_id=${companyId}`;
    }
  }, [activeTab, domain, topbarCompanyId]);

  const getUpcomingEndpoint = useCallback(() => {
    if (activeTab === 0) {
      return `${domain}payroll/operations/payrollprocessing/payrollruncycle/payschedules/?status=Upcoming&company_id=${topbarCompanyId}`;
    }
    return null;
  }, [activeTab, domain, topbarCompanyId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const mainResponse = await fetch(getApiEndpoint(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      });

      if (mainResponse.ok) {
        const mainData = await mainResponse.json();
        const updatedItem = mainData?.find((item) => item.id === payScheduleId);
        setPayScheduleData(updatedItem);
        setApiData(mainData);
        setPayrunData(mainData);
      } else if (mainResponse.status === 409) {
        sessionHandling();
      } else {
        throw new Error(mainResponse.error);
      }

      if (activeTab === 0) {
        const upcomingUrl = getUpcomingEndpoint();
        if (upcomingUrl) {
          const upcomingResponse = await fetch(upcomingUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          });

          if (upcomingResponse.ok) {
            const upcomingDataResult = await upcomingResponse.json();
            setUpcomingData(upcomingDataResult);
            setFilterUpcomingData(upcomingDataResult);
          } else if (upcomingResponse.status === 409) {
            sessionHandling();
          } else {
            throw new Error("Failed to fetch upcoming data");
          }
        }
      } else {
        setUpcomingData([]);
        setFilterUpcomingData([]);
      }
    } catch (error) {
      showToast("error", error?.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    getApiEndpoint,
    getUpcomingEndpoint,
    payScheduleId,
    setPayScheduleData,
    token,
  ]);

  useEffect(() => {
    fetchData();
    setPage(0);
    setUpcomingPage(0);
  }, [fetchData]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleUpcomingChangePage = useCallback((event, newPage) => {
    setUpcomingPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const handleUpcomingChangeRowsPerPage = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setUpcomingRowsPerPage(newRowsPerPage);
    setUpcomingPage(0);
  }, []);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page, upcomingPage]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setPayrunData(filteredData);
      if (page * rowsPerPage >= filteredData.length) {
        setPage(0);
      }
    },
    [page, rowsPerPage]
  );

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  const hanldePaymentBasedOn = (event) => {
    setPaymentBasedOn(event.target.value);
  };

  const initiateCycle = async (data) => {
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/update-payschedule-field/`,
      "PUT",
      {
        pay_schedule_id: payScheduleId,
        payment_basis: paymentBasedOn,
      }
    );
    if (response.ok) {
      setInitiatePayrun(true);
    } else {
      showToast("error", response?.responseDetails?.message);
    }
  };

  const completeUpdateProcess = useCallback(() => {
    setIsProcessing(false);
    setSocketCompleted(true);
    setRecallSummaryApi(true);
  }, [setIsProcessing, setRecallSummaryApi, setSocketCompleted]);

  const connectTaskWebSocket = useCallback(
    (taskId, websocketUrl) => {
      if (socket) {
        socket.close();
      }

      const newSocket = new WebSocket(websocketUrl);
      setSocket(newSocket);

      newSocket.onopen = () => {
        console.log("WebSocket connected for task:", taskId);
      };

      newSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("WebSocket progress update:", data);

          setTaskProgress((prev) => ({
            ...prev,
            progress: data.progress || prev.progress,
            message: data.message || prev.message,
            status: data.status || prev.status,
          }));

          if (data.message) setProcessingMessage(data.message);

          if (data.status === "SUCCESS") {
            setTimeout(() => {
              setRecallEmployeeApi(true);
              setHanldeLoader(false);
              fetchData();
              completeUpdateProcess();
            }, 2000);
          } else if (data.status === "FAILURE") {
            setProcessingMessage(data.message || "Processing failed");

            setTimeout(() => {
              setTaskProgress((prev) => ({ ...prev, active: false }));
              setSocketCompleted(true);
              setIsProcessing(false);
            }, 5000);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      newSocket.onerror = () => {
        setProcessingMessage(
          "Connection issues - processing continues in background"
        );
        setTaskProgress((prev) => ({
          ...prev,
          message: "Connection issues - processing continues in background",
          status: "PROGRESS",
        }));
      };

      newSocket.onclose = () => {
        console.log("WebSocket closed for task:", taskId);

        setProcessingMessage("Connection closed - finalizing...");
        setTimeout(() => {
          setTaskProgress((prev) => ({ ...prev, active: false }));
          setIsProcessing(false);
          setSocketCompleted(true);
        }, 3000);
      };
    },
    [
      socket,
      setSocket,
      setTaskProgress,
      setProcessingMessage,
      setSocketCompleted,
      setRecallEmployeeApi,
      setIsProcessing,
      setHanldeLoader,
      fetchData,
      completeUpdateProcess,
    ]
  );

  useEffect(() => {
    const handleInitiatePayrun = async () => {
      const response = await customAPI(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/intiate_payrun/`,
        "POST",
        {
          pay_schedule_id: payScheduleId,
          payment_based_on: paymentBasedOn,
          use_background_task: true,
          delete_manual_data:true
        }
      );

      if (response.ok) {
        const result = await response.responseDetails;
        if (result.task_id && result.websocket_url) {
          setIsProcessing(true);
          setProcessingMessage("Starting background processing...");
          setTaskProgress({
            active: true,
            progress: 0,
            message: "Starting background processing...",
            status: "PENDING",
            taskId: result.task_id,
            websocketUrl: result.websocket_url,
          });
          setTimeout(() => {
            connectTaskWebSocket(result.task_id, result.websocket_url);
          }, 5000);
        } else {
          completeUpdateProcess();
        }

        showToast("success", response?.responseDetails?.message);
        setViewPayRunScreen(true);
        setInitiatePayrun(false);
        setLogData(response?.responseDetails?.data);
      } else {
        showToast("error", response?.message);
        setInitiatePayrun(false);
      }
    };

    if (initiatePayrun) {
      handleInitiatePayrun();
    }
  }, [
    domain,
    initiatePayrun,
    payScheduleId,
    paymentBasedOn,
    setInitiatePayrun,
    setLogData,
    setViewPayRunScreen,
    completeUpdateProcess,
    connectTaskWebSocket,
    setIsProcessing,
    setProcessingMessage,
    setSocketCompleted,
    setTaskProgress,
  ]);

  return loading && hanldeLoader ? (
    <Loader />
  ) : (
    <Box sx={{ p: 3 }}>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={initiateCycle}
        title={"Initiate Payrun"}
        confirmText={"Continue"}
        showChildren={true}
        cancelText="Cancel"
      >
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Select how employee pay should be calculated
            </Typography>
            <Typography {...infoStyle}>
              Choose whether to calculate pay by Shift or Hours for this pay
              period.
            </Typography>
          </Box>
          <RadioGroup
            row
            value={paymentBasedOn}
            onChange={hanldePaymentBasedOn}
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {paymentOptions.map((opt) => {
              const isActive = paymentBasedOn === opt.id;
              return (
                <Box key={opt.id} sx={cardStyle(isActive)}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Radio value={opt.id} />
                    <Typography
                      fontWeight={"medium"}
                      sx={{
                        color:
                          paymentBasedOn === opt.id
                            ? themes?.stepActiveColor
                            : "inherit",
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      marginLeft: "32px",
                      maxWidth: 250,
                      wordBreak: "break-word",
                    }}
                  >
                    {opt.description}
                  </Typography>
                </Box>
              );
            })}
          </RadioGroup>
        </Box>
      </ConfirmationDialog>
      <Box sx={{ mb: 2 }}>
        <CustomFilter
          title={
            activeTab === 0
              ? "Current Pay Schedule Records"
              : activeTab === 1
              ? "Awaiting Payroll Records"
              : "Completed Pay Schedule Records"
          }
          filterHeaders={payrollCycleHeader}
          data={apiData}
          filteredData={payrunData}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
          showSaveButton={false}
        />
        <Mui.TableContainer
          ref={containerRef}
          sx={{
            maxHeight: activeTab === 0 ? 250 : 500,
            minHeight: activeTab === 0 ? 250 : 500,
          }}
        >
          <Mui.Table className="border">
            <Mui.TableHead sx={stickyHeader}>
              <Mui.TableRow sx={tableRowSize}>
                {payrollCycleHeader?.map((value, index) => (
                  <Mui.TableCell
                    key={index}
                    sx={{
                      color: themes.headLine,
                      fontWeight: "bold",
                    }}
                  >
                    {value?.name}
                  </Mui.TableCell>
                ))}
                {activeTab === 0 ? (
                  <Mui.TableCell
                    sx={{
                      color: themes.headLine,
                      fontWeight: "bold",
                    }}
                  >
                    Action
                  </Mui.TableCell>
                ) : (
                  <Mui.TableCell></Mui.TableCell>
                )}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {payrunData?.length === 0 ? (
                <InfoMessage dataLength={apiData} />
              ) : (
                payrunData
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map((value, index) => (
                    <Mui.TableRow key={index} sx={tableRowSize}>
                      {payrollCycleHeader.map((head, cellIndex) => (
                        <Mui.TableCell
                          key={cellIndex}
                          onClick={() => {
                            setViewPayRunScreen(true);
                            setPayScheduleId(value?.id);
                            setPayScheduleData(value);
                            setGetCompanyId(value?.company_id);
                            setIsClickSchedule(true);
                            setSocketCompleted(value?.status === "Inprogress");
                          }}
                        >
                          {value[head.key] ?? "N/A"}
                        </Mui.TableCell>
                      ))}
                      {activeTab === 0 && value?.status === "Draft" ? (
                        <Mui.TableCell>
                          <PlayCircleOutlineIcon
                            color="primary"
                            onClick={() => {
                              setShowConfirmationDialog(true);
                              setPayScheduleId(value?.id);
                              setPayScheduleData(value);
                              setIsClickSchedule(true);
                              setHanldeLoader(false);
                            }}
                            size="small"
                          />
                        </Mui.TableCell>
                      ) : (
                        <Mui.TableCell></Mui.TableCell>
                      )}
                    </Mui.TableRow>
                  ))
              )}
            </Mui.TableBody>
          </Mui.Table>
          {payrunData?.length > 25 && (
            <Mui.TablePagination
              className="custom-pagination"
              component="div"
              rowsPerPageOptions={[25, 50, 75, 100]}
              count={payrunData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Mui.TableContainer>
      </Box>
      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upcoming Pay Schedule Records : {filterUpcomingData?.length}
          </Typography>
        </Box>
      )}
      <Box>
        {activeTab === 0 && (
          <>
            <Mui.TableContainer sx={{ maxHeight: 250, minHeight: 250 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {upcomingPayrollCycleHeader?.map((value, index) => (
                      <Mui.TableCell
                        key={index}
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {filterUpcomingData?.length === 0 ? (
                    <InfoMessage dataLength={upcomingData} />
                  ) : (
                    filterUpcomingData
                      ?.slice(
                        upcomingPage * upcomingRowsPerPage,
                        upcomingPage * upcomingRowsPerPage + upcomingRowsPerPage
                      )
                      ?.map((value, index) => (
                        <Mui.TableRow key={index} sx={tableRowSize}>
                          {upcomingPayrollCycleHeader.map((head, cellIndex) => (
                            <Mui.TableCell key={cellIndex}>
                              {value[head.key] ?? "N/A"}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
              {filterUpcomingData?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={filterUpcomingData?.length}
                  rowsPerPage={upcomingRowsPerPage}
                  page={upcomingPage}
                  onPageChange={handleUpcomingChangePage}
                  onRowsPerPageChange={handleUpcomingChangeRowsPerPage}
                />
              )}
            </Mui.TableContainer>
          </>
        )}
      </Box>
    </Box>
  );
};
